import { SHELL_API_KEY } from '@platform/parcels/types';

export interface Logger {
  error(message: string, context?: Record<string, unknown>, error?: Error): void;
  warn(message: string, context?: Record<string, unknown>, error?: Error): void;
  info(message: string, context?: Record<string, unknown>, error?: Error): void;
  debug(message: string, context?: Record<string, unknown>, error?: Error): void;
}

/**
 * Shape of the `logging` namespace exposed by the shell on
 * `window['shell/v1/api']`. MFEs should consume this via `getLogger()`; this
 * type is exported for harness/test consumers that touch the window directly.
 */
export interface ShellApiLogging {
  logger: Logger;
  setActiveParcelId?(id: string): () => void;
}

interface ShellApiV1 {
  logging?: ShellApiLogging;
}

function getShellApi(): ShellApiV1 | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as Window & { [SHELL_API_KEY]?: ShellApiV1 })[SHELL_API_KEY];
}

/* eslint-disable no-console */
const consoleLogger: Logger = {
  error: (m, c, e) => console.error(m, c, e),
  warn: (m, c, e) => console.warn(m, c, e),
  info: (m, c, e) => console.info(m, c, e),
  debug: (m, c, e) => console.debug(m, c, e),
};
/* eslint-enable no-console */

/**
 * Returns a typed Logger backed by the shell's Datadog instance. Falls back to
 * console when the shell API is absent (SSR, tests, standalone runs).
 *
 * Call per-use (`getLogger().info(...)`) rather than caching the result at
 * module top-level. The shell API attaches to `window` after module evaluation;
 * a top-level `const log = getLogger()` evaluated before that point would be
 * locked into the console fallback for the lifetime of the module.
 *
 * Log attribution to the correct MFE/parcel is handled by the framework. Apps
 * are attributed by URL automatically; parcels are attributed via
 * `defineParcel({ packageId })`.
 */
export function getLogger(): Logger {
  return getShellApi()?.logging?.logger ?? consoleLogger;
}

/**
 * @internal Auto-wires parcel log attribution. Called by `defineParcel` when the
 * parcel author provides `packageId`. Returns a disposer; invoking it clears the
 * registration only if it still matches (concurrent-mount safe). Returns a no-op
 * disposer when the shell API is absent (tests, standalone).
 */
export function registerParcelAttribution(packageId: string): () => void {
  const setActiveParcelId = getShellApi()?.logging?.setActiveParcelId;
  if (!setActiveParcelId) return () => {};
  return setActiveParcelId(packageId);
}
